/**
 * @fileoverview Wallet Home - Certificate grid/list view
 * Displays all certificates owned by the user
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { useDateFormat } from '@/hooks/useDateFormat';
import { walletService } from '@/services/walletService';
import {
    FileText,
    CheckCircle,
    XCircle,
    AlertCircle,
    Eye,
    Download,
    Shield,
    Calendar,
    Search
} from 'lucide-react';

export const WalletHome = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatDate } = useDateFormat();
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadCertificates();
    }, []);

    const loadCertificates = async () => {
        try {
            setLoading(true);
            const data = await walletService.getCertificates();
            setCertificates(data.certificates || []);
        } catch (err) {
            console.error('Error loading certificates:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredCertificates = certificates.filter(cert =>
        cert.document_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.issuer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const StatusBadge = ({ status, isRevoked }) => {
        if (isRevoked) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    {t('wallet.status.revoked', 'Revocado')}
                </span>
            );
        }

        // Verified on chain = fully verified
        if (status === 'verified_on_chain' || status === 'blockchain_registered' || status === 'finalized' || status === 'registered') {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('wallet.status.verified', 'Verificado')}
                </span>
            );
        }

        // IPFS uploaded = document is on IPFS, pending blockchain
        if (status === 'ipfs_uploaded') {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('wallet.status.ipfs', 'En IPFS')}
                </span>
            );
        }

        // Completed = packaged, pending IPFS/blockchain
        if (status === 'completed' || status === 'svg_generated') {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {t('wallet.status.completed', 'Completado')}
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <AlertCircle className="w-3 h-3 mr-1" />
                {t('wallet.status.processing', 'En Proceso')}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-slate-600">{t('common.loading', 'Loading certificates...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                    <div className="flex items-center space-x-3 text-red-800 mb-2">
                        <XCircle className="w-6 h-6" />
                        <h3 className="font-semibold">{t('common.error', 'Error')}</h3>
                    </div>
                    <p className="text-red-700 text-sm">{error}</p>
                    <button
                        onClick={loadCertificates}
                        className="mt-4 w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t('common.retry', 'Try Again')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        {t('wallet.myDocuments', 'My Certificates')}
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {certificates.length} {t('wallet.totalDocuments', 'documents found')}
                    </p>
                </div>

                {/* Search */}
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder={t('wallet.search', 'Search certificates...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Certificate Grid */}
            {filteredCertificates.length === 0 ? (
                <div className="text-center py-16">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-600 mb-2">
                        {searchTerm
                            ? t('wallet.noResults', 'No certificates found')
                            : t('wallet.noCertificates', 'No certificates yet')}
                    </h3>
                    <p className="text-slate-500">
                        {searchTerm
                            ? t('wallet.tryDifferentSearch', 'Try a different search term')
                            : t('wallet.waitForCertificates', 'Certificates issued to you will appear here')}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCertificates.map((cert) => (
                        <div
                            key={cert.cert_id}
                            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                            onClick={() => navigate(`/wallet/certificate/${cert.cert_id}`)}
                        >
                            {/* Card Header */}
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Shield className="w-5 h-5 text-white" />
                                        <span className="text-white font-semibold text-sm">
                                            {cert.type || t('wallet.certificate', 'Certificate')}
                                        </span>
                                    </div>
                                    <StatusBadge status={cert.status} isRevoked={cert.is_revoked} />
                                </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-4 space-y-3">
                                {/* Document ID */}
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                                        {t('wallet.documentId', 'Document ID')}
                                    </p>
                                    <p className="font-mono text-sm text-slate-900 font-semibold">
                                        {cert.document_id}
                                    </p>
                                </div>

                                {/* Issuer */}
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                                        {t('wallet.issuer', 'Emitido por')}
                                    </p>
                                    <p className="text-sm text-slate-700">
                                        {cert.issuer || t('wallet.unknownIssuer', 'Unknown')}
                                    </p>
                                </div>

                                {/* Date */}
                                <div className="flex items-center space-x-2 text-sm text-slate-600">
                                    <Calendar className="w-4 h-4" />
                                    <span>
                                        {formatDate(cert.issued_at, 'short')}
                                    </span>
                                </div>

                                {/* Blockchain TX Hash */}
                                {cert.blockchain_tx_hash && (
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                                            TX HASH
                                        </p>
                                        <a
                                            href={`https://polygonscan.com/tx/${cert.blockchain_tx_hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline break-all"
                                        >
                                            {cert.blockchain_tx_hash.substring(0, 10)}...{cert.blockchain_tx_hash.substring(cert.blockchain_tx_hash.length - 8)}
                                        </a>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex space-x-2 pt-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/wallet/certificate/${cert.cert_id}`);
                                        }}
                                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                                    >
                                        <Eye className="w-4 h-4" />
                                        <span>{t('wallet.view', 'Ver')}</span>
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`/api/wallet/certificates/${cert.cert_id}/download`, '_blank');
                                        }}
                                        className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                                        title={t('wallet.download', 'Descargar')}
                                    >
                                        <Download className="w-4 h-4 text-slate-600" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
